// The Trade tab's pipeline: what's due, then a board of businesses by stage.
// Drag a card to another column to move it along; click it to open it.
import { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { api } from '../lib/auth';
import { Button, Input, Pill, Stat, StatRow } from '../lib/ui';
import {
  STAGES, STAGE, OPEN_STAGES, accountTotals, pipelineStats, invoiceStats, isFollowUpDue,
  formatGBP, formatPounds, ymd,
} from '../lib/trade';
import { FollowUpTag, errorText } from './TradeCommon';

const CARD = 'trade-account';

function AccountCard({ account, totals, today, onOpen }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: CARD,
    item: { id: account._id, stage: account.stage },
    collect: (m) => ({ isDragging: m.isDragging() }),
  }), [account._id, account.stage]);

  const where = [account.contactName, account.address?.city].filter(Boolean).join(' · ');
  const open = OPEN_STAGES.includes(account.stage);
  return (
    <div
      ref={drag}
      role="button"
      tabIndex={0}
      className={`trade-card${isDragging ? ' is-dragging' : ''}`}
      onClick={() => onOpen(account._id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(account._id); } }}
    >
      <div className="trade-card__name">{account.businessName}</div>
      {where && <div className="trade-card__meta">{where}</div>}
      <div className="trade-card__chips">
        {open && account.potentialWeekly > 0 && <Pill>{formatPounds(account.potentialWeekly)}/wk</Pill>}
        {totals?.last30 > 0 && <Pill tone="ok">{formatPounds(totals.last30)} last 30 days</Pill>}
        {totals?.overdue > 0 && <Pill tone="bad">{formatGBP(totals.overdue)} overdue</Pill>}
        {!totals?.overdue && totals?.unpaid > 0 && <Pill tone="blue">{formatGBP(totals.unpaid)} unpaid</Pill>}
      </div>
      {account.nextStep && (
        <div className="trade-card__next">
          <FollowUpTag account={account} today={today} />
          <span title={account.nextStep}>{account.nextStep}</span>
        </div>
      )}
    </div>
  );
}

function Column({ stage, accounts, totals, today, onOpen, onMove, className = '', children }) {
  const [{ over }, drop] = useDrop(() => ({
    accept: CARD,
    canDrop: (item) => item.stage !== stage.key,
    drop: (item) => onMove(item.id, stage.key),
    collect: (m) => ({ over: m.isOver() && m.canDrop() }),
  }), [stage.key, onMove]);

  return (
    <section ref={drop} className={`trade-col ${className}${over ? ' is-over' : ''}`}>
      {children}
      <div className="trade-col__list">
        {accounts.map(a => (
          <AccountCard key={a._id} account={a} totals={totals[a._id]} today={today} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

// Overdue first, then today's; undated next steps don't nag.
const byNextStep = (a, b) => ymd(a.nextStepDate).localeCompare(ymd(b.nextStepDate));

export default function TradePipeline({ trade, today, flash, onOpen, onAdd }) {
  const [search, setSearch] = useState('');
  const [showLost, setShowLost] = useState(false);
  const { accounts, invoices } = trade;

  const stats = pipelineStats(accounts, invoices, today);
  const money = invoiceStats(invoices, today);
  const totals = accountTotals(invoices, today);
  const due = accounts.filter(a => isFollowUpDue(a, today)).sort(byNextStep);

  const q = search.trim().toLowerCase();
  const visible = accounts.filter(a => !q || [a.businessName, a.contactName, a.address?.city, a.interestedIn]
    .some(v => (v || '').toLowerCase().includes(q)));
  // Within a column: what needs doing soonest at the top, then the most recently touched
  const inStage = (key) => visible.filter(a => a.stage === key).sort((a, b) => {
    const da = ymd(a.nextStepDate) || '9999';
    const db = ymd(b.nextStepDate) || '9999';
    return da === db ? String(b.updatedAt).localeCompare(String(a.updatedAt)) : da.localeCompare(db);
  });
  const lost = inStage('lost');

  const move = async (id, stage) => {
    const before = accounts.find(a => a._id === id);
    if (!before || before.stage === stage) return;
    trade.putAccount({ ...before, stage });
    try {
      const { data } = await api.patch(`/trade/accounts/${id}`, { stage });
      trade.putAccount(data);
      flash(`${data.businessName} moved to ${STAGE[stage].label}`);
    } catch (e) {
      trade.putAccount(before);
      flash(`Couldn't move it: ${errorText(e)}`, 'bad');
    }
  };

  if (!accounts.length) {
    return (
      <div className="trade-empty">
        <strong>No businesses yet</strong>
        Add the first café, restaurant or shop you've talked to, and log how it goes from there.
        <div style={{ marginTop: 14 }}>
          <Button variant="primary" onClick={onAdd}>+ Add business</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StatRow>
        <Stat
          label="Pipeline"
          value={`${formatPounds(stats.potentialWeekly)}/wk`}
          sub={`${stats.openCount} business${stats.openCount === 1 ? '' : 'es'} not ordering yet`}
          accent="var(--accent)"
        />
        <Stat
          label="Customers"
          value={stats.customers}
          sub={`${formatGBP(stats.invoicedLast30)} invoiced in the last 30 days`}
          accent="var(--brand-2)"
        />
        <Stat
          label="Follow-ups due"
          value={stats.dueFollowUps}
          sub={stats.dueFollowUps ? 'today or overdue' : 'nothing due'}
          accent={stats.dueFollowUps ? 'var(--warn)' : undefined}
        />
        <Stat
          label="Unpaid"
          value={formatGBP(money.unpaidTotal)}
          sub={money.overdueCount ? `${money.overdueCount} overdue` : `${money.unpaidCount} invoice${money.unpaidCount === 1 ? '' : 's'}`}
          accent={money.overdueCount ? 'var(--error)' : undefined}
        />
      </StatRow>

      {due.length > 0 && (
        <div className="trade-due">
          <h3>To do</h3>
          <ul>
            {due.map(a => (
              <li key={a._id} onClick={() => onOpen(a._id)}>
                <FollowUpTag account={a} today={today} />
                <b>{a.businessName}</b>
                <span>{a.nextStep}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="trade-toolbar">
        <Input placeholder="Search businesses…" value={search} onChange={e => setSearch(e.target.value)} />
        <span className="muted" style={{ fontSize: 12 }}>Drag a card to another column to move it on.</span>
      </div>

      <div className="trade-board">
        {STAGES.filter(s => s.key !== 'lost').map(stage => {
          const list = inStage(stage.key);
          const sum = OPEN_STAGES.includes(stage.key)
            ? list.reduce((s, a) => s + (a.potentialWeekly || 0), 0)
            : list.reduce((s, a) => s + (totals[a._id]?.last30 || 0), 0);
          return (
            <Column key={stage.key} stage={stage} accounts={list} totals={totals} today={today} onOpen={onOpen} onMove={move}>
              <div className="trade-col__head">
                <h3>{stage.label}</h3>
                <span className="count">{list.length}</span>
                {sum > 0 && (
                  <span className="sum" title={OPEN_STAGES.includes(stage.key) ? 'Potential per week' : 'Invoiced in the last 30 days'}>
                    {formatPounds(sum)}{OPEN_STAGES.includes(stage.key) ? '/wk' : ''}
                  </span>
                )}
              </div>
              {!list.length && <p className="trade-col__hint">{stage.hint}</p>}
            </Column>
          );
        })}
      </div>

      <Column
        stage={STAGE.lost}
        accounts={showLost ? lost : []}
        totals={totals}
        today={today}
        onOpen={onOpen}
        onMove={move}
        className="trade-lost"
      >
        <div className="trade-col__head">
          <h3>Lost</h3>
          <span className="count">{lost.length}</span>
          <Button size="sm" style={{ marginLeft: 'auto' }} onClick={() => setShowLost(s => !s)} disabled={!lost.length}>
            {showLost ? 'Hide' : 'Show'}
          </Button>
        </div>
        {!showLost && <p className="trade-col__hint">Drop a card here when it's a no for now. They stay on file.</p>}
      </Column>
    </div>
  );
}
